import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Heart, Menu, X } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  // Handle scroll after navigation
  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      // Clean up the state
      window.history.replaceState({}, '');
    }
  }, [location]);

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <Heart className="w-8 h-8 text-[#F4B6A1]" />
          <span className="ml-2 text-2xl font-bold text-gray-800 font-['Merriweather']" style={{ letterSpacing: '-0.5px' }}>
            FeelHeard<span className="text-[#C2A4D6]" style={{ fontSize: '0.9em' }}>.me</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <button 
            onClick={() => scrollToSection('pricing')} 
            className="text-gray-700 hover:text-[#B3D9E6] transition-colors"
          >
            Pricing
          </button>
          <button 
            onClick={() => scrollToSection('testimonials')} 
            className="text-gray-700 hover:text-[#B3D9E6] transition-colors"
          >
            Testimonials
          </button>
          <Link to="/signin" className="text-gray-700 hover:text-[#B3D9E6] transition-colors font-['Poppins']">
            Sign In
          </Link>
          <Link to="/register" className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-2 px-6 rounded-full transition-all transform hover:scale-105 shadow-md font-['Poppins']">
            Get Started
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-gray-700"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg absolute top-full left-0 w-full overflow-hidden transition-all duration-300 ease-in-out">
          <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
            <button 
              onClick={() => scrollToSection('pricing')}
              className="text-gray-700 py-2 border-b border-gray-100"
            >
              Pricing
            </button>
            <button 
              onClick={() => scrollToSection('testimonials')}
              className="text-gray-700 py-2 border-b border-gray-100"
            >
              Testimonials
            </button>
            <Link 
              to="/signin"
              className="text-gray-700 py-2 border-b border-gray-100 block text-center font-['Poppins']"
              onClick={() => setIsMenuOpen(false)}
            >
              Sign In
            </Link>
            <Link 
              to="/register"
              className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-2 px-6 rounded-full transition-colors w-full block text-center font-['Poppins']"
              onClick={() => setIsMenuOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;